/* Squalo homepage interactions. All state belongs to a Shopify section instance. */
(() => {
  'use strict';

  const namespace = '__sqHomeRedesign';
  if (window[namespace]) {
    window[namespace].scan(document);
    return;
  }

  const instances = new Map();
  const selector = '[data-sq-home-redesign]';
  const focusable = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function initialize(root) {
    if (instances.has(root)) return;
    const controller = new AbortController();
    const cleanups = [];
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const on = (element, event, handler, options = {}) => {
      if (element) element.addEventListener(event, handler, { ...options, signal: controller.signal });
    };
    const all = (query, scope = root) => Array.from(scope.querySelectorAll(query));
    const behavior = () => reduced.matches ? 'instant' : 'smooth';

    function tabKeyboard(event, tabs, choose) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      const current = tabs.indexOf(event.currentTarget);
      let next = current;
      if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
      if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = tabs.length - 1;
      event.preventDefault();
      tabs[next].focus();
      choose(tabs[next]);
    }

    // Touch and trackpad gestures remain native. Add drag only for mouse pointers.
    all('.sqhr-product-rail, .sqhr-story-rail').forEach(rail => {
      let gesture = null;
      let suppressClick = false;
      on(rail, 'pointerdown', event => {
        suppressClick = false;
        if (event.pointerType !== 'mouse' || event.button !== 0 || rail.scrollWidth <= rail.clientWidth + 1) return;
        gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, left: rail.scrollLeft, dragging: false };
      });
      on(rail, 'pointermove', event => {
        if (!gesture || event.pointerId !== gesture.id) return;
        const dx = event.clientX - gesture.x;
        const dy = event.clientY - gesture.y;
        if (!gesture.dragging) {
          if (Math.abs(dy) > 7 && Math.abs(dy) > Math.abs(dx)) { gesture = null; return; }
          if (Math.abs(dx) < 7) return;
          gesture.dragging = true;
          rail.classList.add('sqhr-is-dragging');
          rail.setPointerCapture(event.pointerId);
        }
        event.preventDefault();
        rail.scrollLeft = gesture.left - dx;
      });
      function finish(event) {
        if (!gesture || event.pointerId !== gesture.id) return;
        const { id, dragging } = gesture;
        gesture = null;
        suppressClick = dragging;
        rail.classList.remove('sqhr-is-dragging');
        if (rail.hasPointerCapture(id)) rail.releasePointerCapture(id);
      }
      on(rail, 'pointerup', finish);
      on(rail, 'pointercancel', finish);
      on(rail, 'lostpointercapture', finish);
      on(rail, 'pointerleave', () => { if (gesture && !gesture.dragging) gesture = null; });
      on(rail, 'dragstart', event => event.preventDefault());
      on(rail, 'click', event => {
        if (!suppressClick || event.detail === 0) return;
        suppressClick = false;
        event.preventDefault();
        event.stopImmediatePropagation();
      }, { capture: true });
    });

    all('.sqhr-shop').forEach(shop => {
      const tabs = all('[data-category-tab]', shop).filter(tab => {
        const panel = document.getElementById(tab.dataset.panel);
        const hasProducts = !!panel?.querySelector('.sqhr-product-card');
        tab.hidden = !hasProducts;
        return hasProducts;
      });
      const panels = all('[data-product-panel]', shop);
      function choose(tab) {
        const panel = panels.find(item => item.id === tab.dataset.panel);
        if (!panel) return;
        panels.forEach(item => { item.hidden = item !== panel; });
        tabs.forEach(item => {
          item.setAttribute('aria-selected', String(item === tab));
          item.tabIndex = item === tab ? 0 : -1;
          if (item.dataset.panel) item.setAttribute('aria-controls', item.dataset.panel);
        });
        const rail = panel.matches('.sqhr-product-rail') ? panel : panel.querySelector('.sqhr-product-rail');
        if (rail) rail.scrollTo({ left: 0, behavior: 'instant' });
      }
      tabs.forEach(tab => {
        on(tab, 'click', () => choose(tab));
        on(tab, 'keydown', event => tabKeyboard(event, tabs, choose));
      });
      if (tabs.length) choose(tabs.find(tab => tab.getAttribute('aria-selected') === 'true') || tabs[0]);
      all('[data-products-direction]', shop).forEach(button => {
        on(button, 'click', () => {
          const rail = all('.sqhr-product-rail', shop).find(item => !item.closest('[hidden]'));
          if (rail) rail.scrollBy({ left: Number(button.dataset.productsDirection) * rail.clientWidth * 0.8, behavior: behavior() });
        });
      });
    });

    const storyRail = root.querySelector('.sqhr-story-rail');
    if (storyRail) {
      const cards = all('.sqhr-story-card', storyRail);
      const controls = all('[data-story-index]');
      const tabs = controls.filter(button => button.closest('.sqhr-story-tabs'));
      const counter = root.querySelector('.sqhr-story-count');
      let index = 0;
      let frame = 0;
      function position(i) {
        return Math.min(storyRail.scrollWidth - storyRail.clientWidth, cards[i].offsetLeft - cards[0].offsetLeft);
      }
      function update() {
        controls.forEach(button => {
          const selected = Number(button.dataset.storyIndex) === index;
          if (button.getAttribute('role') === 'tab') {
            button.setAttribute('aria-selected', String(selected));
            button.tabIndex = selected ? 0 : -1;
          } else {
            button.setAttribute('aria-current', String(selected));
          }
        });
        if (counter) counter.textContent = `${String(index + 1).padStart(2, '0')} / ${String(cards.length).padStart(2, '0')}`;
      }
      function choose(next) {
        if (!cards.length) return;
        index = (next + cards.length) % cards.length;
        storyRail.scrollTo({ left: position(index), behavior: behavior() });
        update();
      }
      controls.forEach(button => on(button, 'click', () => choose(Number(button.dataset.storyIndex))));
      tabs.forEach(tab => on(tab, 'keydown', event => tabKeyboard(event, tabs, next => choose(Number(next.dataset.storyIndex)))));
      all('[data-story-direction]').forEach(button => on(button, 'click', () => choose(index + Number(button.dataset.storyDirection))));
      on(storyRail, 'scroll', () => {
        if (frame || !cards.length) return;
        frame = requestAnimationFrame(() => {
          frame = 0;
          index = cards.reduce((nearest, card, i) => Math.abs(position(i) - storyRail.scrollLeft) < Math.abs(position(nearest) - storyRail.scrollLeft) ? i : nearest, 0);
          update();
        });
      }, { passive: true });
      on(storyRail, 'focusin', event => {
        const focused = cards.findIndex(card => card.contains(event.target));
        if (focused >= 0 && event.target.matches(':focus-visible') && storyRail.scrollWidth > storyRail.clientWidth) choose(focused);
      });
      if (cards.length) update();
      cleanups.push(() => cancelAnimationFrame(frame));
    }

    const community = root.querySelector('.sqhr-community-rail');
    if (community) {
      let paused = reduced.matches;
      let hovering = false;
      let focused = false;
      let visible = false;
      let frame = 0;
      let last = 0;
      let direction = 1;
      let position = community.scrollLeft;
      const canMove = () => !paused && !hovering && !focused && visible && !document.hidden && community.scrollWidth > community.clientWidth;
      function tick(time) {
        frame = 0;
        if (!canMove()) { last = 0; return; }
        const delta = last ? Math.min(time - last, 64) : 0;
        last = time;
        const maximum = community.scrollWidth - community.clientWidth;
        // Keep a floating position: some browsers round each scrollLeft assignment.
        position = Math.max(0, Math.min(maximum, position + delta * 0.018 * direction));
        community.scrollLeft = position;
        if (position >= maximum) direction = -1;
        if (position <= 0) direction = 1;
        frame = requestAnimationFrame(tick);
      }
      function synchronize() {
        if (canMove()) {
          if (!frame) {
            position = community.scrollLeft;
            last = 0;
            frame = requestAnimationFrame(tick);
          }
        } else {
          cancelAnimationFrame(frame);
          frame = 0;
          last = 0;
        }
      }
      function pause() { paused = true; synchronize(); }
      on(community, 'pointerenter', event => { if (event.pointerType === 'mouse') hovering = true; synchronize(); });
      on(community, 'pointerleave', () => { hovering = false; synchronize(); });
      on(community, 'pointerdown', pause);
      on(community, 'wheel', pause, { passive: true });
      on(community, 'keydown', event => { if (['ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown'].includes(event.key)) pause(); });
      on(community, 'focusin', () => { focused = true; synchronize(); });
      on(community, 'focusout', event => { focused = community.contains(event.relatedTarget); synchronize(); });
      on(document, 'visibilitychange', synchronize);
      on(reduced, 'change', () => { if (reduced.matches) pause(); });
      const observer = new IntersectionObserver(entries => {
        visible = entries.some(entry => entry.isIntersecting);
        synchronize();
      }, { threshold: 0.15 });
      observer.observe(community);
      if (window.ResizeObserver) {
        const resize = new ResizeObserver(synchronize);
        resize.observe(community);
        cleanups.push(() => resize.disconnect());
      }
      cleanups.push(() => { observer.disconnect(); cancelAnimationFrame(frame); });
    }

    const dialog = root.querySelector('[data-story-dialog]');
    const video = dialog && dialog.querySelector('video[data-video-source]');
    if (dialog && video) {
      let opener;
      const stopVideo = () => { video.pause(); video.removeAttribute('src'); video.load(); };
      const closeVideo = () => { if (dialog.open) dialog.close(); };
      all('[data-video-open]').forEach(button => on(button, 'click', () => {
        opener = button;
        if (!video.getAttribute('src')) video.src = video.dataset.videoSource;
        if (!dialog.open) dialog.showModal();
        video.play().catch(() => { /* Native controls remain available if autoplay is blocked. */ });
      }));
      all('[data-video-close]', dialog).forEach(button => on(button, 'click', closeVideo));
      on(dialog, 'click', event => {
        if (event.target !== dialog) return;
        const bounds = dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) closeVideo();
      });
      on(dialog, 'cancel', event => { event.preventDefault(); closeVideo(); });
      on(dialog, 'close', () => { stopVideo(); if (opener && opener.isConnected) opener.focus({ preventScroll: true }); });
      on(document, 'visibilitychange', () => { if (document.hidden) video.pause(); });
      cleanups.push(() => { closeVideo(); stopVideo(); });
    }

    const menuButton = root.querySelector('#sxMenuBtn');
    const drawer = root.querySelector('#sxDrawer');
    const overlay = root.querySelector('#sxOverlay');
    if (menuButton && drawer && overlay) {
      let open = false;
      let previousOverflow = '';
      const items = () => all(focusable, drawer).filter(item => item.tabIndex >= 0 && !item.closest('[hidden], [inert], [aria-hidden="true"]') && item.getClientRects().length);
      const closeMenu = (restoreFocus = true) => {
        if (!open) return;
        open = false;
        drawer.classList.remove('open');
        overlay.classList.remove('open');
        drawer.inert = true;
        drawer.setAttribute('aria-hidden', 'true');
        drawer.removeAttribute('aria-modal');
        menuButton.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = previousOverflow;
        if (restoreFocus && menuButton.isConnected) menuButton.focus({ preventScroll: true });
      };
      const openMenu = () => {
        if (open) return;
        open = true;
        previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        drawer.inert = false;
        drawer.setAttribute('role', 'dialog');
        drawer.setAttribute('aria-modal', 'true');
        drawer.setAttribute('aria-hidden', 'false');
        drawer.classList.add('open');
        overlay.classList.add('open');
        menuButton.setAttribute('aria-expanded', 'true');
        (items()[0] || drawer).focus({ preventScroll: true });
      };
      drawer.tabIndex = -1;
      drawer.inert = true;
      drawer.setAttribute('aria-hidden', 'true');
      menuButton.setAttribute('aria-controls', drawer.id);
      menuButton.setAttribute('aria-expanded', 'false');
      on(menuButton, 'click', () => open ? closeMenu() : openMenu());
      on(root.querySelector('#sxDrawerClose'), 'click', () => closeMenu());
      on(overlay, 'click', () => closeMenu());
      on(drawer, 'click', event => { if (event.target.closest('a[href]')) closeMenu(false); });
      on(document, 'keydown', event => {
        if (!open) return;
        if (event.key === 'Escape') { event.preventDefault(); closeMenu(); return; }
        if (event.key !== 'Tab') return;
        const available = items();
        const first = available[0] || drawer;
        const last = available[available.length - 1] || drawer;
        if (!available.length || !drawer.contains(document.activeElement) || (event.shiftKey && document.activeElement === first) || (!event.shiftKey && document.activeElement === last)) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus();
        }
      });
      on(document, 'focusin', event => { if (open && !drawer.contains(event.target)) (items()[0] || drawer).focus(); });
      cleanups.push(() => closeMenu(false));
    }

    instances.set(root, () => {
      controller.abort();
      cleanups.forEach(cleanup => cleanup());
      instances.delete(root);
    });
  }

  function scan(scope) {
    if (scope.matches && scope.matches(selector)) initialize(scope);
    scope.querySelectorAll(selector).forEach(initialize);
  }
  window[namespace] = { scan };
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((destroy, root) => {
      if (event.target === root || event.target.contains(root)) destroy();
    });
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  else scan(document);
})();
